import { lazy, Suspense, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Layout } from "./components/Layout";
import { HomePage } from "./pages/HomePage";
import { CatalogPage } from "./pages/CatalogPage";
import { StaticPage } from "./pages/StaticPage";
import { useAuth } from "./lib/auth";
import { Notice } from "./components/Notice";
import { SeoLandingPage } from "./pages/SeoLandingPage";
import seoRoutesData from "./content/seoRoutes.json";

type SeoRoute = {
  path: string;
  title: string;
  description: string;
};

const SEO_ROUTES = seoRoutesData as Record<string, SeoRoute>;
const DEFAULT_DESCRIPTION = "PremaMarket — этичный маркетплейс объявлений для покупки и продажи товаров и услуг по России.";

function setMetaContent(attribute: "name" | "property", value: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

const LoginPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.RegisterPage })));
const EmailVerifyPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.EmailVerifyPage })));
const ForgotPasswordPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ResetPasswordPage })));
const ListingPage = lazy(() => import("./pages/ListingPage").then((module) => ({ default: module.ListingPage })));
const ListingFormPage = lazy(() => import("./pages/ListingFormPage").then((module) => ({ default: module.ListingFormPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const ChatPage = lazy(() => import("./pages/ChatPage").then((module) => ({ default: module.ChatPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const HelpPage = lazy(() => import("./pages/HelpPage").then((module) => ({ default: module.HelpPage })));
const LegalPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.LegalPage })));

type RouteState = {
  route: string;
  params: URLSearchParams;
};

function parseLocation(): RouteState {
  const url = new URL(window.location.href);
  const queryRoute = url.searchParams.get("route");
  if (queryRoute) {
    return { route: queryRoute, params: url.searchParams };
  }
  const parts = url.pathname.split("/").filter(Boolean);
  if (["business", "help", "how", "kindness", "premamarket-online"].includes(parts[0])) return { route: parts[0], params: url.searchParams };
  if (parts[0] === "legal" && parts[1]) return { route: `legal.${parts[1]}`, params: url.searchParams };
  if (parts[0] === "catalog") return { route: "catalog", params: url.searchParams };
  if (parts[0] === "listing" && parts[1]) {
    url.searchParams.set("id", parts[1]);
    if (parts[2] === "edit") return { route: "listing.edit", params: url.searchParams };
    return { route: "listing", params: url.searchParams };
  }
  if (parts[0] === "chat" && parts[1]) {
    url.searchParams.set("id", parts[1]);
    return { route: "chat", params: url.searchParams };
  }
  if (parts[0] === "user" && parts[1]) {
    url.searchParams.set("id", parts[1]);
    return { route: "profile", params: url.searchParams };
  }
  if (parts[0] === "login") return { route: "login", params: url.searchParams };
  if (parts[0] === "register") return { route: "register", params: url.searchParams };
  if (parts[0] === "dashboard" && parts[1] === "messages" && parts[2] === "archive") {
    return { route: "dashboard.messagesArchive", params: url.searchParams };
  }
  if (parts[0] === "dashboard" && ["messages", "notifications", "favorites", "settings", "resume", "moderation"].includes(parts[1])) {
    return { route: `dashboard.${parts[1]}`, params: url.searchParams };
  }
  if (parts[0] === "dashboard") return { route: "dashboard", params: url.searchParams };
  if (parts[0] === "admin") return { route: "dashboard.moderation", params: url.searchParams };
  return { route: "home", params: url.searchParams };
}

function routePath(route: string, params?: Record<string, string>): string {
  const search = new URLSearchParams(params ?? {});
  if (route === "home") return "/";
  if (["business", "help", "how", "kindness", "premamarket-online"].includes(route)) return `/${route}`;
  if (route.startsWith("legal.")) return `/legal/${route.slice("legal.".length)}`;
  if (route === "catalog") return `/catalog${search.size ? `?${search}` : ""}`;
  if (route === "listing") return `/listing/${params?.id ?? ""}`;
  if (route === "listing.edit") return `/listing/${params?.id ?? ""}/edit`;
  if (route === "chat") return `/chat/${params?.id ?? ""}`;
  if (route === "profile") return `/user/${params?.id ?? ""}`;
  if (route === "login") return `/login${search.size ? `?${search}` : ""}`;
  if (route === "register") return "/register";
  if (route === "dashboard.messages") return "/dashboard/messages";
  if (route === "dashboard.messagesArchive") return "/dashboard/messages/archive";
  if (route === "dashboard.notifications") return "/dashboard/notifications";
  if (route === "dashboard.favorites") return "/dashboard/favorites";
  if (route === "dashboard.settings") return "/dashboard/settings";
  if (route === "dashboard.resume") return "/dashboard/resume";
  if (route === "dashboard.moderation") return `/dashboard/moderation${search.size ? `?${search}` : ""}`;
  if (route === "dashboard") return `/dashboard${search.size ? `?${search}` : ""}`;
  if (route === "admin") return "/dashboard/moderation";
  if (route === "listing.new") return `/?route=listing.create${search.size ? `&${search}` : ""}`;
  if (route === "email.verify") return `/?route=email.verify&${search}`;
  if (route === "password.forgot") return "/?route=password.forgot";
  if (route === "password.reset") return `/?route=password.reset&${search}`;
  return `/?route=${encodeURIComponent(route)}`;
}

function isChunkLoadError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : String(reason ?? "");
  return /dynamically imported module|failed to fetch.*module|importing a module script|chunk/i.test(message);
}

export function App() {
  const [state, setState] = useState<RouteState>(() => parseLocation());
  const { loading, user } = useAuth();

  const go = (route: string, params?: Record<string, string>) => {
    const path = routePath(route, params);
    window.history.pushState(null, "", path);
    setState(parseLocation());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    window.onpopstate = () => setState(parseLocation());
    return () => {
      window.onpopstate = null;
    };
  }, []);

  useEffect(() => {
    if (state.route === "dashboard.moderation" && window.location.pathname === "/admin") {
      window.history.replaceState(null, "", routePath("dashboard.moderation"));
    }
  }, [state.route]);

  useEffect(() => {
    const reloadOnce = () => {
      const key = `premamarket:chunk-reload:${window.location.pathname}${window.location.search}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
      window.location.reload();
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) reloadOnce();
    };
    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error ?? event.message)) reloadOnce();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  const route = state.route;
  const params = state.params;

  useEffect(() => {
    const seo = SEO_ROUTES[route];
    if (!seo) {
      document.title = "PremaMarket";
      setMetaContent("name", "description", DEFAULT_DESCRIPTION);
      setMetaContent("name", "robots", "noindex, follow");
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
      return;
    }

    const canonical = `https://premamarket.ru${seo.path}`;
    document.title = seo.title;
    setMetaContent("name", "description", seo.description);
    setMetaContent("name", "robots", "index, follow");
    setMetaContent("property", "og:title", seo.title);
    setMetaContent("property", "og:description", seo.description);
    setMetaContent("property", "og:url", canonical);
    setCanonical(canonical);
  }, [route]);

  const protectedRoutes = new Set([
    "dashboard",
    "dashboard.messages",
    "dashboard.messagesArchive",
    "dashboard.notifications",
    "dashboard.favorites",
    "dashboard.settings",
    "dashboard.resume",
    "dashboard.moderation",
    "listing.create",
    "listing.edit",
    "chat"
  ]);
  const needsAuth = protectedRoutes.has(route);
  const staticPages: Record<string, { title: string; body: ReactNode }> = {
    business: {
      title: "Для бизнеса",
      body: (
        <>
          <p>PremaMarket помогает небольшим брендам, мастерам и сервисам размещать этичные товары и услуги рядом с частными объявлениями.</p>
          <p>На этапе MVP доступно обычное размещение объявлений. Отдельные бизнес-инструменты, витрины и продвижение будут добавлены позже.</p>
        </>
      )
    },
    how: {
      title: "Как это работает",
      body: (
        <>
          <p>Вы выбираете категорию или используете поиск, открываете объявление и связываетесь с продавцом через телефон или сообщения.</p>
          <p>Чтобы продать товар или услугу, войдите в аккаунт, нажмите «Разместить объявление» и заполните карточку.</p>
        </>
      )
    },
    kindness: {
      title: "#ДоброОбъединяет",
      body: (
        <>
          <p>Это раздел для добрых инициатив, взаимопомощи и проектов, которые поддерживают людей, животных и бережное отношение к планете.</p>
          <p>Полноценная подборка появится после наполнения каталога и модерации первых объявлений.</p>
        </>
      )
    }
  };

  let page: ReactNode;
  if (loading) {
    page = <p className="empty">Загружаем...</p>;
  } else if (needsAuth && !user) {
    page = <Notice kind="error">Нужен вход. <button className="btn btn-ghost" type="button" onClick={() => go("login")}>Войти</button></Notice>;
  } else if (route === "home") {
    page = <HomePage go={go} />;
  } else if (route === "catalog") {
    page = <CatalogPage params={params} go={go} />;
  } else if (route === "listing") {
    page = <ListingPage id={Number(params.get("id"))} go={go} />;
  } else if (route === "listing.create") {
    page = <ListingFormPage go={go} draftId={Number(params.get("draftId")) || null} />;
  } else if (route === "listing.edit") {
    page = <ListingFormPage go={go} editId={Number(params.get("id"))} />;
  } else if (route === "login") {
    page = <LoginPage go={go} params={params} />;
  } else if (route === "register") {
    page = <RegisterPage go={go} />;
  } else if (route === "email.verify") {
    page = <EmailVerifyPage params={params} go={go} />;
  } else if (route === "password.forgot") {
    page = <ForgotPasswordPage go={go} />;
  } else if (route === "password.reset") {
    page = <ResetPasswordPage params={params} go={go} />;
  } else if (route === "dashboard") {
    page = <DashboardPage go={go} view="listings" />;
  } else if (route === "dashboard.messages") {
    page = <DashboardPage go={go} view="messages" />;
  } else if (route === "dashboard.messagesArchive") {
    page = <DashboardPage go={go} view="messagesArchive" />;
  } else if (route === "dashboard.notifications") {
    page = <DashboardPage go={go} view="notifications" params={params} />;
  } else if (route === "dashboard.favorites") {
    page = <DashboardPage go={go} view="favorites" />;
  } else if (route === "dashboard.settings") {
    page = <DashboardPage go={go} view="settings" />;
  } else if (route === "dashboard.resume") {
    page = <DashboardPage go={go} view="resume" />;
  } else if (route === "dashboard.moderation") {
    page = <DashboardPage go={go} view="moderation" params={params} />;
  } else if (route === "chat") {
    page = <ChatPage chatId={Number(params.get("id"))} go={go} />;
  } else if (route === "profile") {
    page = <ProfilePage userId={params.get("id") ?? ""} go={go} />;
  } else if (route === "help") {
    page = <HelpPage go={go} />;
  } else if (route === "premamarket-online") {
    page = <SeoLandingPage go={go} />;
  } else if (route.startsWith("legal.")) {
    page = <LegalPage slug={route.slice("legal.".length)} go={go} />;
  } else if (staticPages[route]) {
    page = <StaticPage title={staticPages[route].title}>{staticPages[route].body}</StaticPage>;
  } else {
    page = <StaticPage title={route} />;
  }

  return (
    <Layout route={route} go={go}>
      <Suspense fallback={<p className="empty">Загружаем...</p>}>
        {page}
      </Suspense>
    </Layout>
  );
}
